/**
 * StepTemplatePicker.tsx  (Step 3)
 *
 * Grid of all available carousel templates.
 * Clicking a card immediately calls /api/carousel/structure (via onPickTemplate)
 * so the user doesn't have to press a separate "Generate" button here.
 *
 * Thumbnails are pre-rendered PNGs (see TEMPLATE_THUMBNAIL_IMAGES). Native
 * <img> tags — no PDF.js parsing, no canvas, no race conditions. The previous
 * approach (live PDF.js render of page 1) was retired due to RI-008 and the
 * silent-failure / cancellation-cascade bugs documented in DistillPageV2.
 */

import React from "react";
import {
  TEMPLATE_CATEGORIES, TEMPLATE_CATEGORY_MAP,
  TEMPLATE_DESCRIPTIONS, TEMPLATE_THUMBNAIL_IMAGES,
  type TemplateCategoryKey,
} from "./constants";
import type { CarouselTemplateMeta } from "./types";
import { primaryBtn, ghostBtn, stepHeading, stepSub, ACCENT, ACCENT_08, ACCENT_12, spinnerStyle } from "./sharedStyles";

// ── Props ─────────────────────────────────────────────────────────────────────

interface Props {
  templates:                    CarouselTemplateMeta[];
  templatesLoaded:              boolean;
  templatesError:               string | null;
  /** Set when /api/carousel/structure fails for a user-picked template. */
  pickError?:                   string | null;
  onDismissPickError?:          () => void;
  selectedTemplateId:           string | null;
  activeCategory:               TemplateCategoryKey;
  isStructureLoading:           boolean;
  /** When set, only templates whose slide range includes this count are shown. */
  preferredSlideCount?:         number | null;
  setActiveCategory:            (c: TemplateCategoryKey) => void;
  onPickTemplate:               (id: string) => void;
  onRetry:                      () => void;
  onBack:                       () => void;
  /** Called when user picks a different slide count in the filter bar. */
  onChangePreferredSlideCount?: (n: number | null) => void;
}

// ── Local styles ──────────────────────────────────────────────────────────────

const categoryPill = (active: boolean): React.CSSProperties => ({
  padding:      "6px 16px",
  borderRadius: "100px",
  fontSize:     "13px",
  fontWeight:   600,
  cursor:       "pointer",
  transition:   "all 0.15s",
  border:       active ? `1.5px solid ${ACCENT}` : "1.5px solid #E4E4E0",
  background:   active ? ACCENT_08 : "#fff",
  color:        active ? ACCENT : "#666",
});

const countChip = (active: boolean): React.CSSProperties => ({
  padding:      "4px 13px",
  borderRadius: "100px",
  fontSize:     "12px",
  fontWeight:   active ? 700 : 500,
  cursor:       "pointer",
  transition:   "all 0.15s",
  border:       active ? `1.5px solid ${ACCENT}` : "1.5px solid #E4E4E0",
  background:   active ? ACCENT_08 : "#fafafa",
  color:        active ? ACCENT : "#555",
  lineHeight:   "1.4",
});

const templateCard = (selected: boolean): React.CSSProperties => ({
  background:    "#fff",
  border:        selected ? `2px solid ${ACCENT}` : "1.5px solid #E4E4E0",
  borderRadius:  "16px",
  overflow:      "hidden",
  breakInside:   "avoid",
  marginBottom:  "16px",
  cursor:        "pointer",
  transition:    "border-color 0.15s, box-shadow 0.15s",
  boxShadow:     selected
    ? `0 0 0 4px ${ACCENT_12}`
    : "0 2px 8px rgba(0,0,0,0.05)",
});

// ── Component ─────────────────────────────────────────────────────────────────

const StepTemplatePicker: React.FC<Props> = ({
  templates, templatesLoaded, templatesError, pickError, onDismissPickError,
  selectedTemplateId,
  activeCategory, isStructureLoading,
  preferredSlideCount,
  setActiveCategory, onPickTemplate, onRetry, onBack,
  onChangePreferredSlideCount,
}) => {
  // 1. Build slide-count options from ALL templates that have a thumbnail.
  //    A template without a thumbnail can't render a preview card, so it's
  //    hidden until a thumb is generated and added to the constants map.
  const templatePool = templates.filter(t => TEMPLATE_THUMBNAIL_IMAGES[t.id]);
  const countSet = new Set<number>();
  templatePool.forEach(t => {
    for (let n = t.slideCountMin; n <= t.slideCountMax; n++) countSet.add(n);
  });
  const sortedCounts = Array.from(countSet).sort((a, b) => a - b);
  const showCountBar  = templatesLoaded && sortedCounts.length > 1 && onChangePreferredSlideCount != null;

  // 2. Category filter + sample-PDF guard
  const byCategory = templatePool.filter(t =>
    activeCategory === "all" || TEMPLATE_CATEGORY_MAP[t.id] === activeCategory
  );

  // 3. Slide count filter — strict, no fallback
  const visible = preferredSlideCount != null
    ? byCategory.filter(t => t.slideCountMin <= preferredSlideCount && preferredSlideCount <= t.slideCountMax)
    : byCategory;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
      <div>
        <p style={stepHeading}>Pick a template</p>
        <p style={stepSub}>Choose how your carousel will look. Click to preview, then generate.</p>
      </div>

      {/* Slide count filter bar */}
      {showCountBar && (
        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          <span style={{ fontSize: "12px", color: "#888", fontWeight: 500 }}>
            {preferredSlideCount != null
              ? `Showing ${preferredSlideCount}-slide templates`
              : "Showing all templates"}
          </span>
          <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
            <button
              style={countChip(preferredSlideCount == null)}
              onClick={() => onChangePreferredSlideCount!(null)}
            >
              Any
            </button>
            {sortedCounts.map(n => (
              <button
                key={n}
                style={countChip(preferredSlideCount === n)}
                onClick={() => onChangePreferredSlideCount!(n)}
              >
                {n} slides
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Category filter tabs */}
      {templates.length > 0 && (
        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "4px" }}>
          {TEMPLATE_CATEGORIES.map(cat => (
            <button
              key={cat.key}
              style={categoryPill(activeCategory === cat.key)}
              onClick={() => setActiveCategory(cat.key)}
              title={cat.hint}
            >
              {cat.label}
            </button>
          ))}
        </div>
      )}

      {/* Inline error banner — shown when /structure fails for a user-picked template.
          The grid stays visible underneath so the user can retry the same template
          (clicking the card again clears the banner) or pick a different one. */}
      {pickError && (
        <div
          role="alert"
          style={{
            background:    "#FFF4F4",
            border:        "1px solid #F2C7C7",
            borderRadius:  "12px",
            padding:       "12px 14px",
            display:       "flex",
            alignItems:    "flex-start",
            gap:           "10px",
            color:         "#9B2C2C",
            fontSize:      "13px",
            lineHeight:    "1.5",
          }}
        >
          <span style={{ flex: "0 0 auto", fontSize: "16px", lineHeight: 1 }}>⚠</span>
          <span style={{ flex: 1 }}>{pickError}</span>
          {onDismissPickError && (
            <button
              type="button"
              onClick={onDismissPickError}
              aria-label="Dismiss error"
              style={{
                flex:        "0 0 auto",
                background:  "transparent",
                border:      "none",
                color:       "#9B2C2C",
                fontSize:    "16px",
                cursor:      "pointer",
                padding:     "0 4px",
                lineHeight:  1,
              }}
            >
              ×
            </button>
          )}
        </div>
      )}

      {/* States: error / loading / empty / grid */}
      {templatesError ? (
        <div style={{ textAlign: "center", padding: "40px 0" }}>
          <p style={{ color: "#c0392b", fontWeight: 600, marginBottom: "8px" }}>⚠ {templatesError}</p>
          <button style={{ ...primaryBtn, fontSize: "13px", padding: "10px 18px" }} onClick={onRetry}>
            Retry
          </button>
        </div>
      ) : !templatesLoaded ? (
        <div style={{ textAlign: "center", color: "#aaa", padding: "40px 0" }}>Loading templates…</div>
      ) : visible.length === 0 ? (
        <div style={{ textAlign: "center", padding: "32px 0", fontSize: "14px" }}>
          {preferredSlideCount != null && showCountBar ? (
            <>
              <p style={{ color: "#888", marginBottom: "10px" }}>
                No {activeCategory !== "all" ? `${activeCategory} ` : ""}templates support {preferredSlideCount} slides.
              </p>
              <p style={{ color: ACCENT, fontSize: "13px", fontWeight: 600 }}>
                Try a different slide count above.
              </p>
            </>
          ) : (
            <p style={{ color: "#aaa" }}>
              {templates.length === 0 ? "No templates available." : "No templates in this category yet."}
            </p>
          )}
        </div>
      ) : (
        /* Masonry-style 2-column grid via CSS columnCount */
        <div style={{ columnCount: 2, columnGap: "16px" }}>
          {visible.map(t => (
            <div
              key={t.id}
              style={templateCard(selectedTemplateId === t.id)}
              onClick={() => onPickTemplate(t.id)}
            >
              {/* Thumbnail: pre-rendered PNG of page 1 of the sample PDF.
                  Generated by Node/src/scripts/generate-template-thumbnails.ts.
                  Native browser <img> — paints as bytes arrive, browser handles
                  loading state, alt-text covers the failure case. */}
              <div style={{
                width: "100%", background: "#f5f4ff",
                overflow: "hidden", borderBottom: "1px solid #E4E4E0",
              }}>
                <img
                  src={TEMPLATE_THUMBNAIL_IMAGES[t.id]}
                  alt={`${t.name} preview`}
                  loading="lazy"
                  style={{ width: "100%", height: "auto", display: "block" }}
                />
              </div>

              <div style={{ padding: "14px 16px" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <p style={{ fontSize: "14px", fontWeight: 700, marginBottom: "4px" }}>{t.name}</p>
                  {selectedTemplateId === t.id && (
                    <span style={{ fontSize: "16px", color: ACCENT }}>✓</span>
                  )}
                </div>
                <p style={{ fontSize: "12px", color: "#888", lineHeight: "1.5" }}>
                  {TEMPLATE_DESCRIPTIONS[t.id] ?? `${t.slideCount} slides`}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Structure API in-flight indicator */}
      {isStructureLoading && (
        <div style={{
          display: "flex", alignItems: "center", gap: "10px",
          padding: "16px 0", color: "#5B4FE8", fontSize: "14px", fontWeight: 600,
        }}>
          <div style={{ ...spinnerStyle, width: "18px", height: "18px" }} />
          Reading your content…
        </div>
      )}

      {!isStructureLoading && (
        <button style={ghostBtn} onClick={onBack}>← Back</button>
      )}
    </div>
  );
};

export default StepTemplatePicker;
